import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import type { LegalDocument } from '../data/legal';
type LegalModalProps = {
  document: LegalDocument | null;
  onClose: () => void;
};
export function LegalModal({
  document: doc,
  onClose
}: LegalModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!doc) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = window.document.body.style.overflow;
    window.document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    closeButtonRef.current?.focus();
    return () => {
      window.document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [doc, onClose]);
  return <AnimatePresence data-id="element-193" data-mp="1cuk5gte71oya">
      {doc && <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center sm:p-6" data-id="element-194" data-mp="1ctiueox6h2gk">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={onClose} className="absolute inset-0 bg-coal-deep/80 backdrop-blur-sm" aria-hidden="true" data-id="element-195" data-mp="1aq6t27pswaqy" />

          <motion.div role="dialog" aria-modal="true" aria-labelledby={`legal-${doc.id}-titulo`} initial={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 24,
        scale: 0.98
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-lift sm:max-h-[85vh] sm:rounded-2xl" data-id="element-196" data-mp="s0gwpg6e3gwx">
            <header className="flex items-start justify-between gap-4 border-b border-sand bg-white px-5 py-4 sm:px-8 sm:py-6" data-id="element-197" data-mp="e6zfi216ega2">
              <div data-id="element-198" data-mp="65i0wkyzxwph">
                <h2 id={`legal-${doc.id}-titulo`} className="text-xl font-bold tracking-tight text-ink sm:text-2xl" data-id="element-199" data-mp="1918vfooey74">
                  {doc.title}
                </h2>
                <p className="mt-1 text-[0.75rem] text-muted sm:text-xs" data-id="element-200" data-mp="ayb6fb2ah92m">{doc.updatedAt}</p>
              </div>
              <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Cerrar" className="-mr-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted outline-none transition-colors duration-150 ease-smooth hover:bg-sand hover:text-ink focus-visible:ring-2 focus-visible:ring-ember" data-id="element-201" data-mp="3jf5wtr1wh0zt">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-202" data-mp="18krzg43zp0ik" />
              </button>
            </header>

            <div className="overflow-y-auto px-5 py-5 sm:px-8 sm:py-7" data-id="element-203" data-mp="13myvlhq5silg">
              <div className="space-y-6" data-id="element-204" data-mp="1b5q2hwlet6u5">
                {doc.sections.map(section => <section key={section.heading} data-id="element-205" data-mp="1g6vrpkfv2byx">
                    <h3 className="text-base font-semibold text-ink" data-id="element-206" data-mp="1g3hs2jyl0gv6">{section.heading}</h3>
                    <div className="mt-2 space-y-2.5" data-id="element-207" data-mp="1285t20de848g">
                      {section.paragraphs.map(paragraph => <p key={paragraph.slice(0, 40)} className="text-[0.95rem] leading-relaxed text-muted" data-id="element-208" data-mp="1txisdi56mbxn">
                          {paragraph}
                        </p>)}
                    </div>
                  </section>)}
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}